/*
 * Keyboard + touch-swipe navigation for the product zoom <dialog>.
 *
 * Keyboard
 *   ArrowLeft  → previous image
 *   ArrowRight → next image
 *   (Esc keeps working natively — handled by the <dialog> element.)
 *
 * Touch swipe
 *   horizontal swipe (touch) on an image at scale 1 → previous / next image.
 *   Ignored while a <drag-zoom-wrapper> is zoomed in: there the pointer is panning.
 *
 * Both call the public API already exposed by zoom-dialog.js:
 *     zoomDialog.selectThumbnail(index, { behavior: 'smooth' })
 * which handles aria-selected, image scroll-into-view, hi-res preload and the
 * `zoom-media-selected` event the counter / caption listen to.
 *
 * On mobile the gallery is forced to overflow-x:hidden in base.css, so we also
 * manually scrollTo() the new slide to make the visual transition happen.
 */

(function () {
  const SWIPE_THRESHOLD_PX = 50;
  const SWIPE_TIME_MS = 600;
  const SWIPE_HORIZONTAL_DOMINANCE = 1.5; // |dx| must exceed |dy| * this

  const editableSelector = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';

  function getOpenZoomDialog() {
    const dialog = document.querySelector('zoom-dialog dialog[open]');
    return dialog ? dialog.closest('zoom-dialog') : null;
  }

  function getCurrentIndex(zoomDialog) {
    const thumbs = zoomDialog.querySelector('.dialog-thumbnails-list');
    if (!thumbs) return 0;
    const buttons = Array.from(thumbs.querySelectorAll('button'));
    const idx = buttons.findIndex(b => b.getAttribute('aria-selected') === 'true');
    return idx >= 0 ? idx : 0;
  }

  function getTotalCount(zoomDialog) {
    return zoomDialog.querySelectorAll('.dialog-zoomed-gallery > .product-media-container').length;
  }

  function navigateBy(zoomDialog, delta) {
    const total = getTotalCount(zoomDialog);
    if (total < 2) return;
    const current = getCurrentIndex(zoomDialog);
    let next = current + delta;
    if (next < 0) next = total - 1;
    if (next >= total) next = 0;

    if (typeof zoomDialog.selectThumbnail === 'function') {
      zoomDialog.selectThumbnail(next, { behavior: 'smooth' });
    }

    // On mobile the gallery has overflow-x: hidden in base.css, so scrollIntoView()
    // inside selectThumbnail() doesn't visually move the slide. Force it.
    if (window.matchMedia('(max-width: 749px)').matches) {
      const gallery = zoomDialog.querySelector('.dialog-zoomed-gallery');
      const items = gallery ? gallery.children : null;
      const target = items ? items[next] : null;
      if (gallery && target) {
        gallery.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      }
    }
  }

  /* -------------------------------------------------------------------------
   * Click navigation (prev/next arrow buttons in the toolbar)
   * ----------------------------------------------------------------------- */

  document.addEventListener('click', (e) => {
    const target = e.target;
    if (!target || !target.closest) return;
    const prevBtn = target.closest('[data-zoom-prev]');
    const nextBtn = target.closest('[data-zoom-next]');
    if (!prevBtn && !nextBtn) return;

    const zoomDialog = (prevBtn || nextBtn).closest('zoom-dialog');
    if (!zoomDialog) return;

    e.preventDefault();
    navigateBy(zoomDialog, prevBtn ? -1 : 1);
  });

  /* -------------------------------------------------------------------------
   * Keyboard navigation
   * ----------------------------------------------------------------------- */

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    if (e.target && e.target.matches && e.target.matches(editableSelector)) return;

    const zoomDialog = getOpenZoomDialog();
    if (!zoomDialog) return;

    e.preventDefault();
    navigateBy(zoomDialog, e.key === 'ArrowLeft' ? -1 : 1);
  });

  /* -------------------------------------------------------------------------
   * Touch swipe navigation
   * ----------------------------------------------------------------------- */

  let swipeStart = null;
  let swipeZoomDialog = null;
  let swipeWrapper = null;

  document.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'touch') return;
    const zoomDialog = getOpenZoomDialog();
    if (!zoomDialog || !zoomDialog.contains(e.target)) return;
    // Only track touches that originated inside the zoom gallery area
    if (!e.target.closest('.dialog-zoomed-gallery')) return;

    swipeStart = { x: e.clientX, y: e.clientY, t: Date.now() };
    swipeZoomDialog = zoomDialog;
    swipeWrapper = e.target.closest('drag-zoom-wrapper');
  }, { passive: true });

  document.addEventListener('pointerup', (e) => {
    if (e.pointerType !== 'touch') return;
    if (!swipeStart || !swipeZoomDialog) return;

    const dx = e.clientX - swipeStart.x;
    const dy = e.clientY - swipeStart.y;
    const dt = Date.now() - swipeStart.t;
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);

    const dlg = swipeZoomDialog;
    const wrapper = swipeWrapper;
    swipeStart = null;
    swipeZoomDialog = null;
    swipeWrapper = null;

    if (wrapper && wrapper.classList.contains('is-zoomed')) return;
    if (dt > SWIPE_TIME_MS) return;
    if (absX < SWIPE_THRESHOLD_PX) return;
    if (absX < absY * SWIPE_HORIZONTAL_DOMINANCE) return;

    navigateBy(dlg, dx < 0 ? 1 : -1);
  }, { passive: true });

  document.addEventListener('pointercancel', () => {
    swipeStart = null;
    swipeZoomDialog = null;
    swipeWrapper = null;
  }, { passive: true });

  // When the dialog closes mid-gesture we don't want stale state.
  window.addEventListener('dialog-close', () => {
    swipeStart = null;
    swipeZoomDialog = null;
    swipeWrapper = null;
  });
})();
